/**
 * Color and material mappings for filter swatches.
 *
 * These mappings convert Algolia facet IDs to visual representations.
 * Supports brand-specific overrides for different visual identities.
 */

import { BRAND, type Brand } from '@/lib/cms/config';

// ============================================================================
// Case Material Mappings
// ============================================================================

/**
 * SVG icons for special case materials (rendered as images instead of color swatches).
 * These are brand-agnostic as they represent physical materials.
 */
export const CASE_SVG_MAPPING: Record<string, string> = {
  TITNM: '/assets/cases/titanium.svg',
  '18KWHGL': '/assets/cases/white-gold.svg',
  PLTNM: '/assets/cases/platinum.svg',
};

/**
 * Case material color gradients.
 * Maps Algolia case IDs to CSS gradient backgrounds.
 */
export const CASE_COLOR_MAPPING: Record<string, string> = {
  // Stainless steel
  STSTL: 'linear-gradient(137.96deg, #747474 -9.54%, #FCFCFC 50.22%, #757575 96.28%)',

  // Stainless steel & 18k red gold
  STNSTL_18KREG:
    'linear-gradient(140.19deg, #9B9B9B 12.43%, #FFFFFF 41.4%, #FDFDFD 46.72%, #B3886C 46.91%, rgba(251, 236, 221, 0.99) 53.23%, #B08467 69.38%, #58382E 85.68%)',

  // 18k red gold
  '18KREG':
    'linear-gradient(143.88deg, #8F5F41 15.14%, rgba(194, 157, 132, 0.997558) 33.25%, rgba(251, 236, 221, 0.99) 50.2%, #B08467 73.53%, #58382E 82.07%)',

  // Breitlight®
  BREITL:
    'linear-gradient(142.88deg, #252525 15.8%, #616161 34.93%, #858585 49.82%, #515151 64.48%, #181818 78.71%)',

  // DLC-coated titanium
  DLC_CTI: 'linear-gradient(141.17deg, #232323 15.51%, #A9A9A9 48.18%, #2D2D2D 88.43%)',

  // Stainless steel & 18k rose gold
  STNSTL_18KROG:
    'linear-gradient(140.19deg, #9B9B9B 13.76%, #FFFFFF 43.14%, #FDFDFD 46.72%, #D38F6A 46.91%, #FFFBF4 53.23%, #D69E7D 80.79%)',

  // Stainless steel & tungsten
  SS_TNG:
    'linear-gradient(140.19deg, #9B9B9B 11.89%, #FFFFFF 43.9%, #FDFDFD 46.72%, #626468 46.91%, #B4BCBD 53.23%, #303334 80.79%)',

  // Ceramic
  CERAM_GEM: '#efefef',

  // DLC-coated stainless steel
  DLCCSSTL: 'linear-gradient(141.17deg, #232323 15.51%, #9B9B9B 48.18%, #2D2D2D 88.43%)',

  // Small scale traceable 18K red gold
  BETTERGOLD:
    'linear-gradient(145.71deg, #8F5F41 13.58%, rgba(194, 157, 132, 0.997558) 25.23%, rgba(251, 236, 221, 0.99) 49.91%, #B08467 78.8%, #855F4B 86.78%, #58382E 99.5%)',

  // Stainless steel & platinum
  SS_PLAT:
    'linear-gradient(140.19deg, #9B9B9B 16.58%, #FFFFFF 45.14%, #FDFDFD 46.72%, #CECECE 46.91%, #F1F1F1 51.25%, #ABADB1 79.25%)',

  // Stainless steel (gem-set)
  STNSTL_GEM: 'linear-gradient(137.96deg, #747474 -9.54%, #FCFCFC 50.22%, #757575 96.28%)',

  // Stainless steel & 18k red gold (gem-set)
  STNSTL_18KREG_GEM:
    'linear-gradient(140.19deg, #9B9B9B 12.43%, #FFFFFF 41.4%, #FDFDFD 46.72%, #B3886C 46.91%, rgba(251, 236, 221, 0.99) 53.23%, #B08467 69.38%, #58382E 85.68%)',

  // Bronze
  BRNZ: 'linear-gradient(318.58deg, #6C3C04 13.35%, #F8CF96 50.92%, #AC8651 77.26%)',

  // 18k yellow gold
  '18KYEGLD':
    'linear-gradient(144.66deg, #AF8230 7.78%, #FCF1AB 48.96%, #D1B467 76%, #FCF1AB 95.41%, #C39F50 108.53%)',

  // 18k yellow gold (gem-set)
  '18KYELGLD_GEM':
    'linear-gradient(143.88deg, #8F5F41 15.14%, rgba(194, 157, 132, 0.997558) 33.25%, rgba(251, 236, 221, 0.99) 50.2%, #B08467 73.53%, #58382E 82.07%)',

  // Stainless steel & 18k grey gold (gem-set)
  SS_18KGRG_GEM: 'linear-gradient(137.96deg, #747474 -9.54%, #FCFCFC 50.22%, #757575 96.28%)',

  // Stainless steel & 18k white gold
  SS_18KWHGLD: 'linear-gradient(137.96deg, #747474 -9.54%, #FCFCFC 50.22%, #EAEAEA 96.28%)',
};

// ============================================================================
// Dial / Strap filter swatches
// ============================================================================

/**
 * Gallet filter-icon palette (90° gradients).
 * Shared by dial / strap / bracelet swatches where names overlap.
 */
const FILTER_SWATCH = {
  black: 'linear-gradient(90deg, #46484A 0%, #070809 100%)',
  anthracite: 'linear-gradient(90deg, #696D6F 0%, #242729 100%)',
  grey: 'linear-gradient(90deg, #BEC0C1 0%, #5B5F62 100%)',
  darkBrown: 'linear-gradient(90deg, #684631 0%, #2B1A12 100%)',
  brown: 'linear-gradient(90deg, #9E6841 0%, #512F1D 100%)',
  cognac: 'linear-gradient(90deg, #D38B42 0%, #7E4119 100%)',
  orange: 'linear-gradient(90deg, #FFAE43 0%, #CF4B0A 100%)',
  blue: 'linear-gradient(90deg, #4584CD 0%, #12376B 100%)',
  darkBlue: 'linear-gradient(90deg, #263F5E 0%, #1D3048 50%, #101D30 100%)',
  green: 'linear-gradient(90deg, #539165 0%, #18452A 100%)',
  lightBeige: 'linear-gradient(90deg, #F0E0BE 0%, #BEA478 100%)',
  skyBlue: 'linear-gradient(90deg, #C2E4F2 0%, #4F97BB 100%)',
  iceBlue: 'linear-gradient(90deg, #E6F8FC 0%, #89C3D2 100%)',
  white: 'linear-gradient(90deg, #FFFFFF 0%, #D8DAD7 100%)',
  silverMetal:
    'linear-gradient(90deg, #6C7175 0%, #EEF1F2 20%, #969B9F 40%, #FAFBFB 60%, #858A8E 80%, #676D71 100%)',
} as const;

/** Dial color gradients — maps Algolia dial color IDs to CSS backgrounds. */
export const DIAL_COLOR_MAPPING: Record<string, string> = {
  'V-DIAL-COLOR-5664': FILTER_SWATCH.anthracite, // Anthracite
  'V-DIAL-COLOR-AQUA': 'linear-gradient(210deg, #88B7CB, #7EA9BC)', // Aquamarine
  'V-DIAL-COLOR-5969': FILTER_SWATCH.lightBeige, // Beige / Light beige
  'V-DIAL-COLOR-5714': FILTER_SWATCH.blue, // Blue
  'V-DIAL-COLOR-5716': 'linear-gradient(210deg, #D6E5E7, #9AC7C8)', // Blue mother-of-pearl
  'V-DIAL-COLOR-5686': FILTER_SWATCH.black, // Black
  'V-DIAL-COLOR-5687': 'linear-gradient(210deg, #4F4F4F, #2A2A2A)', // Black mother-of-pearl
  'V-DIAL-COLOR-5720': 'linear-gradient(210deg, #5A4541 0%, #150300 100%)', // Bronze
  'V-DIAL-COLOR-5723': FILTER_SWATCH.brown, // Brown
  'V-DIAL-COLOR-9696': 'linear-gradient(210deg, #541D2D, #380411)', // Burgundy
  'V-DIAL-COLOR-CHAMP': 'linear-gradient(225deg, #E4B978 0%, #A7813E 100%)', // Champagne
  'V-DIAL-COLOR-5765556362': 'linear-gradient(210deg, #2F2F2F 0%, #070707 100%)', // Carbon
  'V-DIAL-COLOR-5842': 'linear-gradient(225deg, #EFC89E 0%, #D2AF96 100%)', // Cream
  'V-DIAL-COLO-999': FILTER_SWATCH.cognac, // Copper / Cognac
  'V-DIAL-COLOR-44555162': FILTER_SWATCH.grey, // Dove grey / Grey
  'V-DIAL-COLOR-5765': FILTER_SWATCH.green, // Green
  'V-DIAL-COLOR-1111': FILTER_SWATCH.iceBlue, // Ice blue
  'V-DIAL-COLOR-SKBL': FILTER_SWATCH.skyBlue, // Sky blue
  'V-DIAL-COLOR-LATTE': 'linear-gradient(210deg, #DBD3CA, #C1B096)', // Iced latte
  'V-DIAL-COLOR-445566': FILTER_SWATCH.darkBlue, // Midnight / Dark blue
  'V-DIAL-COLOR-8888': 'linear-gradient(210deg, #AFBE9F, #8B987B)', // Mint green
  'V-DIAL-COLOR-5791': 'linear-gradient(210deg, #FBF6F2, #EBE8E3)', // Mother-of-pearl
  'V-DIAL-CLOR-MUS': 'linear-gradient(210deg, #D58600 0%, #A85900 100%)', // Mustard
  'V-DIAL-COLOR-5800': FILTER_SWATCH.orange, // Orange
  'V-DIAL-COLOR-7777': 'linear-gradient(210deg, #F7B9AD 0%, #C67C70 100%)', // Pink
  'V-DIAL-COLOR-6666': 'linear-gradient(210deg, #E0C7C6 0%, #CA9492 100%)', // Pink mother-of-pearl
  'V-DIAL-COLOR-66477271': 'linear-gradient(210deg, #62C2B4 0%, #009D80 100%)', // Peppermint
  'V-DIAL-COLOR-443322': 'linear-gradient(210deg, #B1C48C 0%, #8E9F6B 100%)', // Pistachio green
  'V-DIAL-COLOR-556644': 'linear-gradient(210deg, #B23B3B 0%, #6d2222 100%)', // Red
  'V-DIAL-COLOR-6366444211': 'linear-gradient(210deg, #AC8C70, #B38A5F)', // Sand
  'V-DIAL-COLOR-5843': FILTER_SWATCH.silverMetal, // Silver
  VDIALCOLOR9999: 'linear-gradient(210deg, #FFC400 0%, #FFC400 100%)', // Yellow
  'V-DIAL-COLOR-9876': 'linear-gradient(210deg, #009AAA, #008EA4)', // Turquoise
  'V-DIAL-WALNUT': FILTER_SWATCH.darkBrown, // Walnut / Dark brown
  'V-DIAL-COLOR-5926': FILTER_SWATCH.white, // White
};

// ============================================================================
// Strap Color Mappings
// ============================================================================

/**
 * Strap color gradients.
 * Maps Algolia strap color IDs to CSS gradient backgrounds.
 */
export const STRAP_COLOR_MAPPING: Record<string, string> = {
  'V-STRAP-COLOR-5664': FILTER_SWATCH.anthracite, // Anthracite
  'V-STRAP-COLOR-AQUA': 'linear-gradient(210deg, #88B7CB, #7EA9BC)', // Aquamarine
  V_STRAP_COLOR_BEIGE: FILTER_SWATCH.lightBeige, // Beige / Light beige
  'V-STRAP-COLOR-5714': FILTER_SWATCH.blue, // Blue
  'V-STRAP-COLOR-5686': FILTER_SWATCH.black, // Black
  'V-STRAP-COLOR-5723': FILTER_SWATCH.brown, // Brown
  V_STRAP_COLOR_BURG: 'linear-gradient(210deg, #541D2D, #380411)', // Burgundy
  'V-STRAP-COLOR-G18': FILTER_SWATCH.cognac, // Gold brown / Cognac
  'V-STRAP-COLOR-5765': FILTER_SWATCH.green, // Green
  'V-STRAP-COLOR-GREY': FILTER_SWATCH.grey, // Grey
  'V-STRAP-COLOR-LATTE': 'linear-gradient(210deg, #DBD3CA, #C1B096)', // Iced latte
  'V-STRAP-COLOR-5779': 'linear-gradient(210deg, #8A865D 0%, #4a4831 100%)', // Khaki green
  'V-STRAP-COLOR-6666': FILTER_SWATCH.silverMetal, // Metal bracelet / Silver metal
  'V-STRAP-COLOR-MINT': 'linear-gradient(210deg, #AFBE9F, #8B987B)', // Mint green
  'V-STRAP-COLOR-5800': FILTER_SWATCH.orange, // Orange
  V_STRAP_COLOR_PINK: 'linear-gradient(210deg, #F7B9AD 0%, #C67C70 100%)', // Pink
  V_STRAP_COLOR_RED: 'linear-gradient(210deg, #B23B3B 0%, #6d2222 100%)', // Red
  VSTRAPCOLOR5929: 'linear-gradient(210deg, #AC8C70, #B38A5F)', // Sand
  V_STRAP_COLOR_YELLOW: 'linear-gradient(210deg, #FFC400 0%, #FFC400 100%)', // Yellow
  'V-SRAP-COLOR-TANGERINE': 'linear-gradient(210deg, #F96812, #EB5A08)', // Tangerine
  'V-STRAP-COLOR-9876': 'linear-gradient(210deg, #009AAA, #008EA4)', // Turquoise
  'V-STRAP-COLOR-5926': FILTER_SWATCH.white, // White
};

/**
 * PIM category color refs (`CATEGORY-COLOR-*`) → existing strap color IDs.
 * Used when bracelet_category_color pipe IDs use external_ref rather than V-STRAP-COLOR-*.
 * PRE also ships prefixed IDs: `WBR_CATEGORY_COLORS-CATEGORY-COLOR-*`.
 */
const BRACELET_CATEGORY_COLOR_ALIASES: Record<string, keyof typeof STRAP_COLOR_MAPPING | string> = {
  'CATEGORY-COLOR-ANTHRACITE': 'V-STRAP-COLOR-5664',
  'CATEGORY-COLOR-AQUA': 'V-STRAP-COLOR-AQUA',
  'CATEGORY-COLOR-AQUAMARINE': 'V-STRAP-COLOR-AQUA',
  'CATEGORY-COLOR-BEIGE': 'V_STRAP_COLOR_BEIGE',
  'CATEGORY-COLOR-BLUE': 'V-STRAP-COLOR-5714',
  'CATEGORY-COLOR-BLACK': 'V-STRAP-COLOR-5686',
  'CATEGORY-COLOR-BROWN': 'V-STRAP-COLOR-5723',
  'CATEGORY-COLOR-BURGUNDY': 'V_STRAP_COLOR_BURG',
  'CATEGORY-COLOR-GREEN': 'V-STRAP-COLOR-5765',
  'CATEGORY-COLOR-GREY': 'V-STRAP-COLOR-GREY',
  'CATEGORY-COLOR-ORANGE': 'V-STRAP-COLOR-5800',
  'CATEGORY-COLOR-PINK': 'V_STRAP_COLOR_PINK',
  'CATEGORY-COLOR-RED': 'V_STRAP_COLOR_RED',
  'CATEGORY-COLOR-WHITE': 'V-STRAP-COLOR-5926',
  'CATEGORY-COLOR-YELLOW': 'V_STRAP_COLOR_YELLOW',
  'CATEGORY-COLOR-TURQUOISE': 'V-STRAP-COLOR-9876',
  'CATEGORY-COLOR-MINT': 'V-STRAP-COLOR-MINT',
  'CATEGORY-COLOR-SAND': 'VSTRAPCOLOR5929',
  'CATEGORY-COLOR-TANGERINE': 'V-SRAP-COLOR-TANGERINE',
  'CATEGORY-COLOR-LATTE': 'V-STRAP-COLOR-LATTE',
  'CATEGORY-COLOR-KHAKI': 'V-STRAP-COLOR-5779',
};

const WBR_CATEGORY_COLORS_PREFIX = 'WBR_CATEGORY_COLORS-';

/**
 * Color mapping for watch bracelet_category_color facets.
 * Supports V-STRAP-COLOR-*, CATEGORY-COLOR-*, and WBR_CATEGORY_COLORS-CATEGORY-COLOR-* (BEC-24331).
 */
export const BRACELET_CATEGORY_COLOR_MAPPING: Record<string, string> = {
  ...STRAP_COLOR_MAPPING,
  ...Object.fromEntries(
    Object.entries(BRACELET_CATEGORY_COLOR_ALIASES).flatMap(([alias, strapId]) => {
      const color = STRAP_COLOR_MAPPING[strapId];
      if (!color) return [];
      return [
        [alias, color],
        [`${WBR_CATEGORY_COLORS_PREFIX}${alias}`, color],
      ];
    })
  ),
};

// ============================================================================
// Eyewear Color Mappings
// ============================================================================

/**
 * Frame color gradients for eyewear.
 * Maps Algolia frame color values to CSS gradient backgrounds.
 */
export const FRAME_COLOR_MAPPING: Record<string, string> = {
  'Silver & black':
    'repeating-linear-gradient(210deg, #A9A9A9 0%, #A9A9A9 10%, #2D2D2D 10%, #2D2D2D 20%)',
  'Dark grey': 'linear-gradient(210deg, #2D2D2D 0%, #1A1A1A 100%)',
  Havana: 'linear-gradient(210deg, #A0522D 0%, #7B4B3A 100%)',
  SLBLK: 'linear-gradient(210deg, #A9A9A9 0%, #2D2D2D 100%)',
};

/**
 * Lens color gradients for eyewear.
 * Maps Algolia lens color IDs to CSS gradient backgrounds.
 */
export const LENS_COLOR_MAPPING: Record<string, string> = {
  // Standard lens colors with MER prefix
  'MER_LENS_COLOUR-BLUE': FILTER_SWATCH.blue, // Blue
  'MER_LENS_COLOUR-GRBL': 'linear-gradient(210deg, #A3C9E2 0%, #3B82B2 100%)', // Gradient blue
  'MER_LENS_COLOUR-GREEN': 'linear-gradient(210deg, #5C6E5D 0%, #3F4D43 100%)', // Green
  'MER_LENS_COLOUR-GRADIENTGREY': 'linear-gradient(210deg, #D1D1D1 0%, #6B6B6B 100%)', // Gradient grey
  'MER_LENS_COLOUR-GRADIENTGREEN': 'linear-gradient(210deg, #8FA37B 0%, #3E4B3D 100%)', // Gradient green
  'MER_LENS_COLOUR-GRDRBL': 'linear-gradient(210deg, #2C4A72 0%, #1A2E4F 100%)', // Dark gradient blue
  'MER_LENS_COLOUR-DARKGREY': 'linear-gradient(210deg, #6E6E6E 0%, #404040 100%)', // Dark grey
  'MER_LENS_COLOUR-GRGRPNK': 'linear-gradient(210deg, #A8A8A8 0%, #F2C6CF 100%)', // Grey to pink gradient

  // Short-form IDs
  GREY: 'linear-gradient(210deg, #A0A0A0 0%, #6B6B6B 100%)', // Grey
  GRDRBL: 'linear-gradient(210deg, #A3C9E2 0%, #3B82B2 100%)', // Gradient blue
  GRGRN: 'linear-gradient(210deg, #5C6E5D 0%, #3F4D43 100%)', // Gradient green

  // Zeiss lens colors
  'GREEN LIGHT PRO': 'linear-gradient(210deg, #C3D4A3 0%, #8FA37B 100%)', // Green Light Pro
  'ENS_COLOUR-GREENZEISSLIGHTPRO': 'linear-gradient(210deg, #D0E0D0 0%, #A8C0A8 100%)', // Zeiss Green Light Pro

  // Fallback to dial color ID pattern (some lens colors share dial color IDs)
  'V-DIAL-COLOR-5714': FILTER_SWATCH.blue, // Blue
};

// ============================================================================
// Collection Image Mappings (Brand-Specific)
// ============================================================================

/**
 * Breitling collection images.
 */
export const BREITLING_COLLECTION_IMAGES: Record<string, string> = {
  Navitimer: '/assets/watches/breitling/navitimer.png',
  Chronomat: '/assets/watches/breitling/chronomat.png',
  Avenger: '/assets/watches/breitling/avenger.png',
  Superocean: '/assets/watches/breitling/superocean.png',
  'Superocean Heritage': '/assets/watches/breitling/superocean-heritage.png',
  Premier: '/assets/watches/breitling/premier.png',
  Professional: '/assets/watches/breitling/professional.png',
  'Classic AVI': '/assets/watches/breitling/classic-avi.png',
  'Top Time': '/assets/watches/breitling/toptime.png',
};

/**
 * Gallet collection images.
 * Add Gallet-specific collection images here as they become available.
 */
export const GALLET_COLLECTION_IMAGES: Record<string, string> = {
  // Placeholder - add Gallet collections as they become available
  // 'MultiChron': '/assets/watches/gallet/multichron.png',
  // 'Flying Officer': '/assets/watches/gallet/flying-officer.png',
};

/**
 * Get collection images for the current brand.
 */
export function getCollectionImages(brand: Brand = BRAND): Record<string, string> {
  switch (brand) {
    case 'breitling':
      return BREITLING_COLLECTION_IMAGES;
    case 'gallet':
      return GALLET_COLLECTION_IMAGES;
    default:
      return {};
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the color/gradient for a case material ID.
 * Returns undefined if no mapping exists.
 */
export function getCaseColor(id: string): string | undefined {
  return CASE_COLOR_MAPPING[id];
}

/**
 * Get the SVG icon path for a case material ID.
 * Returns undefined if no icon exists (use color instead).
 */
export function getCaseSvg(id: string): string | undefined {
  return CASE_SVG_MAPPING[id];
}

/**
 * Get the color/gradient for a dial color ID.
 * Returns undefined if no mapping exists.
 */
export function getDialColor(id: string): string | undefined {
  return DIAL_COLOR_MAPPING[id];
}

/**
 * Get the color/gradient for a strap color ID.
 * Returns undefined if no mapping exists.
 */
export function getStrapColor(id: string): string | undefined {
  return STRAP_COLOR_MAPPING[id];
}

/**
 * Get the color/gradient for a frame color ID.
 * Returns undefined if no mapping exists.
 */
export function getFrameColor(id: string): string | undefined {
  return FRAME_COLOR_MAPPING[id];
}

/**
 * Get the color/gradient for a lens color ID.
 * Returns undefined if no mapping exists.
 */
export function getLensColor(id: string): string | undefined {
  return LENS_COLOR_MAPPING[id];
}

/**
 * Get the appropriate color mapping for a facet type.
 */
export function getColorMapping(
  facetType:
    | 'case'
    | 'dial_color'
    | 'color'
    | 'bracelet_category_color'
    | 'frame_color'
    | 'lens_color'
): Record<string, string> {
  switch (facetType) {
    case 'case':
      return CASE_COLOR_MAPPING;
    case 'dial_color':
      return DIAL_COLOR_MAPPING;
    case 'color':
      return STRAP_COLOR_MAPPING;
    case 'bracelet_category_color':
      return BRACELET_CATEGORY_COLOR_MAPPING;
    case 'frame_color':
      return FRAME_COLOR_MAPPING;
    case 'lens_color':
      return LENS_COLOR_MAPPING;
    default:
      return {};
  }
}

/**
 * Check if a facet type should use swatch display.
 */
export function isSwatchFacet(facetName: string): boolean {
  return [
    'case',
    'dial_color',
    'color',
    'bracelet_category_color',
    'frame_color',
    'lens_color',
  ].includes(facetName);
}

/**
 * Check if a facet type should use collection image display.
 */
export function isCollectionFacet(facetName: string): boolean {
  return facetName === 'collection';
}
